import React from 'react';
import { Avatar } from '@backstage/core-components';
import {
  Box,
  IconButton,
  Link,
  makeStyles,
  Tooltip,
  Theme,
  Typography,
  useTheme,
} from '@material-ui/core';
import EditRounded from '@material-ui/icons/EditRounded';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import PersonIcon from '@material-ui/icons/Person';

export interface AgentCardProps {
  id?: string;
  title: string;
  description: string;
  avatarColor: string;
  useCases: Array<{ name: string; color: string }>;
  agentType: string;
  agentTypeColor: string;
  owner: string;
  ownerEmail?: string;
  createdBy?: string;
  segment?: string;
  aiCategory?: string;
  approvalStatus: 'Approved' | 'Under Review' | 'Rejected' | 'Resubmit';
  updatedTime: string;
  reviewer?: string;
  bookmarked?: boolean;
  onBookmarkToggle?: () => void;
  canEdit?: boolean;
  onEdit?: () => void;
  onClick?: () => void;
  isNew?: boolean;
  modalContent?: {
    overview: string[];
    capabilities: string[];
    targetAudience: {
      designedFor: string;
      businessGroup: string;
      geography: string;
      geographicDetails?: string;
      segment?: string;
      lob?: string;
      aiCategory?: string;
    };
    whatYouProvide: string[];
    whatYouGet: string[];
    governance: {
      dataAccess: string;
      compliance: string;
      validationSummary: string;
      validatedBy: string;
      version: string;
      importantToKnow: string;
      evidenceLink?: string;
      evidenceFileName?: string;
      evidenceFilePath?: string;
      evidenceFileType?: string;
      evidenceFileSize?: number;
    };
    integrationMethod?: {
      method?: string;
      configuration?: string;
      additionalSystems?: string;
    };
    agentConfiguration?: {
      agentUrl?: string;
      agentVersion?: string;
      skills?: string[];
      systemCapabilities?: string[];
    };
    interactionSettings?: {
      inputFormat?: string;
      responseFormat?: string;
      requiresSecureAccess?: boolean;
    };
    connectionDetails?: {
      apiEndpoint?: string;
      authenticationMethod?: string;
      connectionType?: string;
      maximumInputSize?: string;
      responseTime?: string;
    };
    releaseNotes: Array<{
      version: string;
      notes: string[];
    }>;
  };
}

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    padding: 20,
    transition: 'box-shadow 0.2s ease, outline 0.2s ease, transform 0.1s ease',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    outline: `1px solid ${theme.palette.border}`,
    '&:hover': {
      boxShadow: theme.shadows[2],
      outline: `1px solid ${theme.palette.border}`,
    },
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flex: 1,
    minWidth: 0,
  },
  avatar: {
    flexShrink: 0,
  },
  title: {
    margin: 0,
    fontSize: 18,
    fontWeight: 700,
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  newBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '3px 10px',
    borderRadius: 999,
    backgroundColor: theme.palette.success.main,
    color: theme.palette.primary.contrastText,
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    marginRight: 8,
  },
  bookmarkButton: {
    padding: 4,
    color: theme.palette.text.tertiary,
  },
  bookmarkButtonActive: {
    color: theme.palette.primary.main,
  },
  actionsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  editIconButton: {
    padding: 4,
    color: theme.palette.primary.main,
  },
  description: {
    margin: '0 0 40px 0',
    fontSize: 14,
    color: theme.palette.text.tertiary,
    lineHeight: 1.7,
    flex: 1,
  },
  footer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    marginTop: 'auto',
  },
  infoGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  infoLabel: {
    fontSize: 10,
    color: theme.palette.text.tertiary,
    fontWeight: 400,
    letterSpacing: 0.5,
  },
  badgesRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  outlinedBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '1px 8px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 500,
    backgroundColor:
      theme.palette.type === 'dark' ? '#465855' : theme.palette.infoBackground,
    color: theme.palette.text.secondary,
    border: 'none',
    width: 'fit-content',
  },
  assistantTypeBadge: {
    alignSelf: 'flex-start',
  },
  ownerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  ownerIcon: {
    fontSize: 16,
    color: theme.palette.text.tertiary,
  },
  ownerText: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  ownerLink: {
    textDecoration: 'underline',
    color: theme.palette.success.main,
  },
  approvalInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  approvalBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '1px 8px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  separatorDot: {
    fontSize: 12,
    color: theme.palette.text.tertiary,
  },
  updatedTime: {
    fontSize: 10,
    color: theme.palette.text.tertiary,
  },
}));

export const getApprovalColor = (status: string, theme: Theme) => {
  switch (status) {
    case 'Approved':
      return theme.palette.type === 'dark'
        ? '#13591A'
        : theme.palette.successBackground;
    case 'Under Review':
      return theme.palette.warningBackground;
    default:
      return theme.palette.background.secondary;
  }
};

export const getApprovalIconColor = (status: string, theme: Theme) => {
  switch (status) {
    case 'Approved':
      return theme.palette.success.main;
    case 'Under Review':
      return theme.palette.warning.main;
    default:
      return theme.palette.text.tertiary;
  }
};

export const getApprovalTextColor = (status: string, theme: Theme) => {
  switch (status) {
    case 'Approved':
      return theme.palette.text.primary;
    case 'Under Review':
      return theme.palette.text.primary;
    default:
      return theme.palette.text.secondary;
  }
};

export const getApprovalIcon = (status: string, color: string, iconForeground: string) => {
  switch (status) {
    case 'Approved':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="9" fill={color} />
          <path d="M9 12L11 14L15 10" stroke={iconForeground} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'Under Review':
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="9" fill={color} />
          <path d="M12 8V12L15 15" stroke={iconForeground} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    default:
      return null;
  }
};

export const AgentCard: React.FC<AgentCardProps> = ({
  title,
  description,
  avatarColor,
  useCases,
  agentType,
  aiCategory,
  owner,
  ownerEmail,
  approvalStatus,
  updatedTime,
  bookmarked = false,
  onBookmarkToggle,
  canEdit = false,
  onEdit,
  onClick,
  isNew = false,
}) => {
  const classes = useStyles();
  const theme = useTheme<Theme>();

  const handleBookmarkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onBookmarkToggle?.();
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.();
  };

  return (
    <Box className={classes.card} onClick={onClick}>
      <Box className={classes.cardHeader}>
        <Box className={classes.titleRow}>
          <Avatar
            displayName={title}
            customStyles={{
              width: '40px',
              height: '40px',
              minWidth: '40px',
              borderRadius: '50%',
              backgroundColor: avatarColor,
              color: theme.palette.primary.contrastText,
              fontWeight: 600,
              fontSize: '14px',
            }}
          />
          <Typography className={classes.title} component="h3">
            {title}
          </Typography>
        </Box>
        {isNew && <Box className={classes.newBadge}>NEW</Box>}
        <Box className={classes.actionsRow}>
          {canEdit && (
            <Tooltip title="Edit">
              <IconButton
                size="small"
                className={classes.editIconButton}
                onClick={handleEditClick}
                aria-label="Edit"
              >
                <EditRounded color="primary" fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton
            className={`${classes.bookmarkButton} ${bookmarked ? classes.bookmarkButtonActive : ''}`}
            onClick={handleBookmarkClick}
            aria-label="Bookmark"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M17 3H7C5.89543 3 5 3.89543 5 5V21L12 18L19 21V5C19 3.89543 18.1046 3 17 3Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill={bookmarked ? 'currentColor' : 'none'}
              />
            </svg>
          </IconButton>
        </Box>
      </Box>

      <Typography className={classes.description}>{description}</Typography>

      <Box className={classes.footer}>
        <Box className={classes.infoGroup}>
          <Typography className={classes.infoLabel}>Use Case</Typography>
          <Box className={classes.badgesRow}>
            {useCases.map((useCase, index) => (
              <Box key={index} className={classes.outlinedBadge}>
                {useCase.name}
              </Box>
            ))}
          </Box>
        </Box>

        <Box className={classes.infoGroup}>
          <Typography className={classes.infoLabel}>Agent Type</Typography>
          <Box className={`${classes.outlinedBadge} ${classes.assistantTypeBadge}`}>
            {aiCategory || agentType}
          </Box>
        </Box>

        <Box className={classes.infoGroup}>
          <Typography className={classes.infoLabel}>Owner/Team</Typography>
          <Box className={classes.ownerInfo}>
            {ownerEmail ? (
              <>
                <MailOutlineIcon className={classes.ownerIcon} />
                <Link href={`mailto:${ownerEmail}`} className={classes.ownerLink} title="Contact Owner">
                  {owner}
                </Link>
              </>
            ) : (
              <>
                <PersonIcon className={classes.ownerIcon} />
                <Typography className={classes.ownerText}>{owner}</Typography>
              </>
            )}
          </Box>
        </Box>

        <Box className={classes.infoGroup}>
          <Typography className={classes.infoLabel}>Approval Status</Typography>
          <Box className={classes.approvalInfo}>
            <Box className={classes.approvalBadge} style={{ backgroundColor: getApprovalColor(approvalStatus, theme), color: getApprovalTextColor(approvalStatus, theme) }}>
              {getApprovalIcon(
                approvalStatus,
                getApprovalIconColor(approvalStatus, theme),
                theme.palette.primary.contrastText,
              )}
              {approvalStatus}
            </Box>
            <Typography className={classes.separatorDot}>•</Typography>
            <Typography className={classes.updatedTime}>{updatedTime}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
